import { Injectable } from '@angular/core';
import {
    CablePinTemperatureMetrics,
    ChargingStationsApiService,
    GetChargingStationResponse,
    GetChargingStationsTreeResponse,
    GetStationCommissioningResponse,
    GetWarrantyHistoryResponse,
    PickSystemMetricsRow,
    PowerModuleDetails,
    SatelliteMetrics,
    StationInfo,
    StatusRow,
} from 'api/charging_stations.api';
import { AlertProblemService } from 'shared/alert-problem-service/alert-problem.service';
import {
    EvseApiService,
    LatestTransactionRow,
    GetEvsesForChargingStationResponse,
    GetEvsesResponse,
} from '@kempoweroyj/ngx-evse-api-client';
import { NotificationsService, RowTypes } from 'api/notifications.api';
import { QueryRequest, RawQuery, ReportingApiService } from '@kempoweroyj/ngx-reporting-api-client';
import { cloneDeep, get as _get, groupBy, keyBy, merge, sortBy } from 'lodash';
import { BehaviorSubject, combineLatest, EMPTY, from, interval, Observable, of, ReplaySubject, Subject } from 'rxjs';
import {
    auditTime,
    distinctUntilChanged,
    exhaustMap,
    filter,
    map,
    mapTo,
    publishReplay,
    refCount,
    startWith,
    switchMap,
    switchMapTo,
    take,
    tap,
    throttleTime,
} from 'rxjs/operators';
import moment from 'moment';
import { parseCode } from '@kempoweroyj/ce-lib-product-code';
import { Transaction, TransactionSample, TransactionsApiService } from '@kempoweroyj/ngx-transactions-api-client';
import { SettingsService } from './settings.service';
import { TxCsmsEventBody } from 'api/events/tx';
import { retryWithDelay } from 'shared/utils/retryWithDelay';
import { AuthService } from 'providers/auth.service';
import { ConfigService } from 'providers/config.service';
import { SearchApiService, StationSearchResponse } from '@kempoweroyj/ngx-search-api-client';
import { IamUsersApiService } from '@kempoweroyj/ngx-iam-users-api-client';
import { IamUsersApiService as IamUsersService } from 'api/user.service';

// cabinets
import { DataForPmcs } from 'shared/dialogs/cabinet-dialog/cabinet/cabinet.component';
import {
    extractPmcCanId,
    NON_FATAL_PMC_PROBLEMS,
    mapPmcAlerts,
} from 'shared/dialogs/cabinet-dialog/pmc-list/pmc-list.component';
import { VehiclesService } from 'shared/stations-service/vehicles.service';
import { Vehicle } from '@kempoweroyj/ngx-vehicles-api-client';
import * as localforage from 'localforage';
import { CsmsNodeExtended, GetChargingStationsTreeResponseExtended } from 'api/charging_stations.api.extensions';
import { WarrantyHistory } from '@kempoweroyj/ngx-charging-stations-api-client';
import { Severity } from '@kempoweroyj/ngx-error-codes-api-client';

export const REFRESH_ON_EVENT_TIME_MS = 5000;

const FORCE_REFRESH_INTERVAL = false;
const FORCE_REFRESH_INTERVAL_MS = 5000;

interface CachedStationsEntry {
    ts: string;
    stage: string;
    tenantId: string;
    res: GetChargingStationsTreeResponse;
}

export type StationType = PickSystemMetricsRow['type'] | 'CSTATION' | 'UNKNOWN' | 'VIRTUAL';
export interface Cabinet {
    /** 0 for main cabinet, -1, -2 for slaves */
    id: number;
    /** e.g. M, 1, 2 */
    displayId: string;
    type: 'MAIN' | 'SECONDARY';
    metrics?: SatelliteMetrics;
    error: boolean;
    errorSeverity: Severity;
}
export interface CabinetObservable {
    id: number;
    displayId: string;
    cabinetMetrics?: SatelliteMetrics;
    pmcStatus?: SatelliteMetrics['pmcStatus'];
    pmcDevices?: GetChargingStationResponse['devices'];
    dataForPmcs?: DataForPmcs;
    missingData?: boolean;
}
export interface Satellite {
    type: PickSystemMetricsRow['type'];
    actualType: StationType;
    station: GetChargingStationResponse;
    satelliteId: number;
    connectors: GetEvsesForChargingStationResponse;
    metrics: SatelliteMetrics;
    serialNumber?: string;
    tooltipText?: string;
}

export interface ConnectorStats {
    insertionCycles: number;
    energyExpendedKwh: number;
    operatingHours: number;
    daysSinceFirstUse: number;
    /** percentage of sessions that did not begin charging */
    unstartedRate: number;
}
export interface ConnectorInfo {
    satelliteId: number;
    connectorId: number;
    connectorSequenceNo: number;
    cableLength: number;
    temp: CablePinTemperatureMetrics;
}

export interface GetChargingStationsTreeResponseWithCacheStatus extends GetChargingStationsTreeResponseExtended {
    /** Is rest of the data from cache or not */
    fromCache?: boolean;
}

export interface GetWarrantyHistoryResponseWithUserName extends GetWarrantyHistoryResponse {
    modifiedUserName?: string;
}

export interface WarrantyHistoryWithUserName extends WarrantyHistory {
    modifiedUserName?: string;
}

/**
 * Caches station information fetching
 */
@Injectable({
    providedIn: 'root',
})
export class StationsService {
    private refreshRequested$ = new Subject<void>();

    private _stationObservableCache: { [stationId: string]: Observable<GetChargingStationResponse> } = {};
    private _connectorsObservableCache: { [stationId: string]: Observable<GetEvsesForChargingStationResponse> } = {};
    private _evsesObservableCache: { [stationId: string]: Observable<GetEvsesResponse> } = {};
    private _warrantyHistoryObservableCache: {
        [stationId: string]: Observable<GetWarrantyHistoryResponseWithUserName>;
    } = {};
    private _stationRefreshRequest$: { [stationId: string]: Subject<void> } = {};
    private _warrantyRefreshRequest$: { [stationId: string]: Subject<void> } = {};
    private _stationsLoading$ = new BehaviorSubject<boolean>(true);
    private _connectorsLoading$ = new BehaviorSubject<boolean>(true);
    loading$ = combineLatest([this._stationsLoading$, this._connectorsLoading$]).pipe(map(([a, b]) => a || b));

    private _seekTime$ = new BehaviorSubject<string>(null);
    get seekTime() {
        return this._seekTime$.value;
    }
    seekTime$: Observable<string> = this._seekTime$.pipe(distinctUntilChanged());
    isHistoric$ = this._seekTime$.pipe(
        map((seekTime) => !!seekTime),
        distinctUntilChanged()
    );
    private _historicStationLoading$ = new BehaviorSubject(false);
    private _historicConnectorsLoading$ = new BehaviorSubject(false);
    historicLoading$ = combineLatest([this._historicStationLoading$, this._historicConnectorsLoading$]).pipe(
        map(([a, b]) => a || b)
    );

    highlightedTxId$ = new BehaviorSubject<string>(undefined);
    highlightedEvse$ = new BehaviorSubject<{ stationId: string; evseId: number | string } | undefined>(undefined);
    highlightedStationId$ = new BehaviorSubject<string>(undefined);

    constructor(
        private authService: AuthService,
        private configService: ConfigService,
        private evseApi: EvseApiService,
        private notifications: NotificationsService,
        private reportingApi: ReportingApiService,
        private searchApi: SearchApiService,
        private settingsService: SettingsService,
        private stationsApi: ChargingStationsApiService,
        private transactionApi: TransactionsApiService,
        private vehiclesService: VehiclesService,
        private chargingStationsApi: ChargingStationsApiService,
        private iamUsersApi: IamUsersApiService,
        private iamUsersService: IamUsersService,
        private alertProblemService: AlertProblemService
    ) {}

    stationRefreshRequest$(stationId: string) {
        if (!this._stationRefreshRequest$[stationId]) {
            // TODO: replace this with something more sensible...
            // subscriptions to stationIds will accumulate as the user browses through ChargEye
            // pushing forward with this, as it's better than the current situation, which is that user gets all notifications
            this.notifications.connected$.pipe(filter((connected) => connected)).subscribe(() => {
                this.notifications.subscribeForever({ stationIds: [stationId] });
            });

            this._stationRefreshRequest$[stationId] = new Subject<void>();
            this.notifications.notification$
                .pipe(filter((n) => n.type === 'ChargingStationUpdated' && n.stationId === stationId))
                .subscribe(() => {
                    this._stationRefreshRequest$[stationId].next();
                });
            this.notifications.connected$.pipe(filter((connected) => connected)).subscribe(() => {
                this._stationRefreshRequest$[stationId].next();
            });

            // Force refresh
            if (FORCE_REFRESH_INTERVAL) {
                interval(FORCE_REFRESH_INTERVAL_MS).subscribe(() => {
                    this._stationRefreshRequest$[stationId].next();
                });
            }
        }
        return this._stationRefreshRequest$[stationId];
    }
    warrantyRefreshRequest$(stationId: string) {
        if (!this._warrantyRefreshRequest$[stationId]) {
            this._warrantyRefreshRequest$[stationId] = new Subject<void>();

            // Force refresh
            if (FORCE_REFRESH_INTERVAL) {
                interval(FORCE_REFRESH_INTERVAL_MS).subscribe(() => {
                    this._warrantyRefreshRequest$[stationId].next();
                });
            }
        }
        return this._warrantyRefreshRequest$[stationId];
    }

    getStationSerial(station: GetChargingStationResponse) {
        return station?.devices?.find((d) => d.deviceTypeShort === 'cs')?.serialNumber;
    }

    async getStations(stationIds: string[]) {
        const promises: Promise<StationInfo | void>[] = [];
        for (const stationId of stationIds) {
            const p = this.station$(stationId)
                .pipe(take(1))
                .toPromise()
                .then((station) => station.station as StationInfo)
                .catch((err) => {
                    console.error(err);
                });
            promises.push(p);
        }
        const stations = (await Promise.all(promises)).filter((s) => !!s) as StationInfo[];

        return stations;
    }

    private async getCacheKey() {
        await this.authService.userIsAuthed();
        return `stationsCache-${this.authService.profile?.tenantId}-${this.configService.config.envName}`;
    }
    private async getCachedStations(): Promise<GetChargingStationsTreeResponse> {
        try {
            const json = await localforage.getItem<string>(await this.getCacheKey());

            const cacheEntry = JSON.parse(json) as CachedStationsEntry;
            return cacheEntry?.res;
        } catch (e) {
            console.error(e);
            return undefined;
        }
    }

    private async updateCachedStations(res: GetChargingStationsTreeResponse): Promise<void> {
        try {
            const cacheEntry: CachedStationsEntry = {
                res,
                tenantId: this.authService.profile?.tenantId,
                stage: this.configService.config.envName,
                ts: new Date().toISOString(),
            };
            const json = JSON.stringify(cacheEntry);
            await localforage.setItem(await this.getCacheKey(), json);
        } catch (e) {
            console.error(e);
        }
    }

    /**
     * Refreshes the cached stations, so that next time stations$() is called, it will fetch fresh data
     */
    refreshCachedStations(): void {
        this._stations$ = null;
    }

    private mapStationSearchResponseToGetChargingStationsTreeResponseWithCacheStatus(
        stationSearchResponse: StationSearchResponse
    ): GetChargingStationsTreeResponseWithCacheStatus {
        return {
            tree: stationSearchResponse.results.map((res) => {
                return {
                    alertSummary: res.alertSummary,
                    data: {
                        compactExtras: {
                            labelValues: res.station.labels?.map((label) => label.value),
                        },
                        evses: res.connectors.map((connector) => {
                            return {
                                actualEVSEId: connector.evse?.actualEVSEId,
                                evseId: connector.evseId,
                                evseName: connector.evseName,
                                evseType: connector.evseType,
                                roamingEvseId: connector.evse?.roamingEvseId,
                            };
                        }),
                        station: {
                            customer: {
                                name: res.station.manufacturingInfo?.partnerName,
                            },
                            firmwareVersion: res.station.firmwareVersion,
                            location: {
                                name: res.location?.name,
                                address: res.location?.address,
                            },
                            locationUid: res.location?.id,
                            model: res.station.model,
                            name: res.station.name,
                            operationalStatus: res.station.operationalStatus,
                            stationId: res.station.stationId,
                            manufacturingInfo: {
                                orderNumber: res.station.manufacturingInfo?.orderNumber,
                            },
                        },
                    },
                    id: res.station.stationId,
                    name: res.station.name,
                    serialNumber: res.station.manufacturingInfo?.serialNumber,
                    type: 'station',
                } as CsmsNodeExtended;
            }),
        } as GetChargingStationsTreeResponseWithCacheStatus;
    }

    /** Stations cache, may or may not contain station information when queried */
    stations: GetChargingStationsTreeResponseWithCacheStatus | undefined;
    private _stations$: ReplaySubject<GetChargingStationsTreeResponseWithCacheStatus>;
    stations$(): Observable<GetChargingStationsTreeResponseWithCacheStatus> {
        if (this._stations$) {
            return this._stations$.asObservable();
        } else {
            this._stations$ = new ReplaySubject<GetChargingStationsTreeResponseWithCacheStatus>(1);

            const locallyCachedStations$ = from(this.getCachedStations());
            const stationsChanged$ = this.notifications.notification$.pipe(
                filter(
                    (n) =>
                        n.type === 'ChargingStationUpdated' &&
                        (['station', 'connection'] as RowTypes[]).some((c) => n.changedSchemas.includes(c))
                )
            );
            const connected$ = this.notifications.connected$.pipe(
                filter((connected) => connected),
                distinctUntilChanged()
            );

            const fetchStations$ = (locallyCachedStations: GetChargingStationsTreeResponse) =>
                merge(connected$, stationsChanged$).pipe(
                    startWith([]),
                    tap(() => {
                        this._stationsLoading$.next(true);
                    }),
                    exhaustMap(() =>
                        this.searchApi
                            .stationSearch()
                            .then((res) =>
                                this.mapStationSearchResponseToGetChargingStationsTreeResponseWithCacheStatus(res)
                            )
                    ),
                    tap(() => {
                        this._stationsLoading$.next(false);
                    }),
                    filter((v) => v?.tree?.length >= 0),
                    tap((res) => this.updateCachedStations(res)),
                    startWith({
                        ...locallyCachedStations,
                        fromCache: true,
                    } as GetChargingStationsTreeResponseWithCacheStatus),
                    filter((v) => v?.tree?.length >= 0),
                    retryWithDelay<GetChargingStationsTreeResponseWithCacheStatus>()
                );

            return locallyCachedStations$.pipe(
                switchMap((locallyCachedStations) => fetchStations$(locallyCachedStations)),
                tap((stations) => {
                    this._stations$.next(stations);
                    this.stations = cloneDeep(stations);
                })
            );
        }
    }

    stationsLoading$(): Observable<boolean> {
        return this._stationsLoading$.asObservable();
    }

    station$(stationId: string): Observable<GetChargingStationResponse> {
        if (!this._stationObservableCache[stationId]) {
            let cached: GetChargingStationResponse;
            this._stationObservableCache[stationId] = this.stationRefreshRequest$(stationId).pipe(
                startWith([]),
                switchMapTo(this.seekTime$),
                switchMap((seekTime) => {
                    if (seekTime && cached) {
                        this._historicStationLoading$.next(true);
                        return this.fetchHistoricStation(stationId, seekTime, cached).finally(() =>
                            this._historicStationLoading$.next(false)
                        );
                    } else {
                        if (!stationId) {
                            return of(null); // Return an empty observable
                        } else {
                            this._stationsLoading$.next(true);

                            return this.stationsApi
                                .getChargingStation(stationId, { stationTenants: true })
                                .then((res) => {
                                    cached = res;
                                    return res;
                                })
                                .catch((e) => {
                                    console.error('station$', e);
                                    return Promise.reject<GetChargingStationResponse>(e);
                                })
                                .finally(() => {
                                    this._stationsLoading$.next(false);
                                });
                        }
                    }
                }),
                retryWithDelay(),
                publishReplay(1),
                refCount()
            );
        }

        return this._stationObservableCache[stationId];
    }

    invalidateStation$Cache(stationId: string) {
        this._stationObservableCache[stationId] = null;
    }

    stationSettings$(stationId: string) {
        return this.settingsService.settingsShouldBeRefreshed$(stationId).pipe(
            startWith(null),
            switchMap(() => from(this.settingsService.getStationSettings(stationId)))
        );
    }

    evses$(stationId: string, ignoreCache?: boolean): Observable<GetEvsesResponse> {
        if (!this._evsesObservableCache[stationId] || ignoreCache) {
            this._evsesObservableCache[stationId] = this.stationRefreshRequest$(stationId).pipe(
                auditTime(REFRESH_ON_EVENT_TIME_MS),
                startWith([]),
                switchMap(() => {
                    return this.evseApi.getEvses(stationId).catch(() => {
                        return undefined;
                    });
                }),
                filter((res) => !!res),
                publishReplay(1),
                refCount()
            );
        }

        return this._evsesObservableCache[stationId];
    }

    /**
     * Creates a cached warranty history and returns enriched Observable with user name
     * @param stationId
     * @param ignoreCache
     * @returns Observable: GetWarrantyHistoryResponseWithUserName
     */
    warrantyHistory$(stationId: string, ignoreCache?: boolean): Observable<GetWarrantyHistoryResponseWithUserName> {
        if (!this._warrantyHistoryObservableCache[stationId] || ignoreCache) {
            this._warrantyHistoryObservableCache[stationId] = this.warrantyRefreshRequest$(stationId).pipe(
                startWith([]),
                switchMap(() => {
                    return combineLatest([
                        // fetch users modifying warranty history (if sysadmin)
                        this.authService.authenticated$.pipe(
                            distinctUntilChanged(),
                            filter((auth) => auth),
                            switchMap(() => {
                                const isSystemAdmin = this.authService.getPermissions().isSystemAdmin;

                                if (isSystemAdmin) {
                                    return this.iamUsersService.getAll(false); // force fetch users & update cache
                                }

                                return of([]); // return empty array observable in other cases
                            })
                        ),
                        // fetch warranty rows
                        this.chargingStationsApi.getWarrantyHistory(stationId).catch(() => {
                            return undefined;
                        }),
                    ]);
                }),
                map(([, warranty]) => {
                    // loop through warranty history and fetch from fresh cache
                    (warranty.history as WarrantyHistoryWithUserName[]).forEach((warr) => {
                        const user = this.iamUsersService.getFromCache(warr.modifiedUserId); // get users from cache
                        warr.modifiedUserName = user?.name;
                    });

                    return warranty;
                }),
                filter((res) => !!res),
                publishReplay(1),
                refCount()
            );
        }

        return this._warrantyHistoryObservableCache[stationId];
    }
    refreshWarrantyCache(stationId: string) {
        this._warrantyRefreshRequest$[stationId].next();
    }

    lastWarrantyHistory$(stationId: string) {
        return this.warrantyHistory$(stationId).pipe(map((warranty) => warranty.history?.[0]));
    }

    evse$(stationId: string, actualEvseId: number) {
        return this.evses$(stationId).pipe(
            map((evses) => evses?.evses?.find((evse) => evse.actualEVSEId === actualEvseId))
        );
    }

    evseByConnectorId$(stationId: string, evseId: number | string) {
        return this.evses$(stationId).pipe(
            // tslint:disable-next-line: triple-equals
            map((evses) => evses?.evses?.find((evse) => evse.connectors?.some((c) => c.evseId == evseId)))
        );
    }

    connectors$(stationId: string): Observable<GetEvsesForChargingStationResponse> {
        if (!this._connectorsObservableCache[stationId]) {
            let cached: GetEvsesForChargingStationResponse;
            this._connectorsObservableCache[stationId] = this.stationRefreshRequest$(stationId).pipe(
                startWith([]),
                throttleTime(2000, undefined, { leading: true, trailing: true }),
                switchMapTo(this.seekTime$),
                switchMap((seekTime) => {
                    if (seekTime && cached) {
                        this._historicConnectorsLoading$.next(true);
                        return this.fetchHistoricConnectors(stationId, seekTime, cached).finally(() =>
                            this._historicConnectorsLoading$.next(false)
                        );
                    } else {
                        this._connectorsLoading$.next(true);
                        return this.evseApi
                            .getEvsesForChargingStation(stationId)
                            .then((res) => {
                                cached = res;
                                return res;
                            })
                            .catch(() => {
                                return undefined;
                            })
                            .finally(() => {
                                this._connectorsLoading$.next(false);
                            });
                    }
                }),
                filter((res) => !!res),
                retryWithDelay(),
                publishReplay(1),
                refCount()
            );
        }

        return this._connectorsObservableCache[stationId];
    }

    connector$(stationId: string, evseId: number | string) {
        return this.connectors$(stationId).pipe(
            map((connectors) =>
                connectors.find((c) => c.evseId === (isNaN(evseId as number) ? evseId : String(evseId)))
            )
        );
    }

    getStationType(station: GetChargingStationResponse): StationType {
        const mainMetrics = station?.metrics?.find(
            (m) => m.type === 'CABINET' || m.type === 'TROLLEY' || m.type === 'STANDALONE'
        );
        if (mainMetrics) {
            switch (mainMetrics.type) {
                case 'TROLLEY':
                    return 'TROLLEY';
                case 'STANDALONE':
                    return 'STANDALONE';
                default:
                    return 'SATELLITE';
            }
        }

        return station?.station?.model?.startsWith('C') ? 'SATELLITE' : 'UNKNOWN';
    }

    getTrolleyAndCabinetId(station: GetChargingStationResponse) {
        const mainCabinet = station?.metrics?.find((d) => d.type === 'CABINET');
        const trolley = station?.metrics?.find((d) => d.type === 'TROLLEY');
        const secondaryCabinets = station?.metrics?.filter((d) => d.type === 'SLAVE_CABINET');
        const scDevices = station?.devices?.filter((d) => d.deviceTypeShort === 'sc');

        const metrics = [mainCabinet, trolley, ...secondaryCabinets].filter((m) => m !== undefined);

        const ids = metrics
            .filter((m) => !scDevices.find((d) => d.satelliteId === m.satelliteId)?.stale)
            .map((m) => {
                return {
                    id: m.satelliteId,
                    type: m.type,
                };
            });

        return ids;
    }

    async getCabinets(station: GetChargingStationResponse): Promise<Cabinet[]> {
        const mainCabinet = station?.metrics?.find((d) => d.type === 'CABINET');
        const secondaryCabinets = station?.metrics?.filter((d) => d.type === 'SLAVE_CABINET');
        const scDevices = station?.devices?.filter((d) => d.deviceTypeShort === 'sc');
        const pmcs = station?.devices?.filter((d) => d.deviceTypeShort === 'pm' && !d.stale);

        const metricsForAllCabinets = [mainCabinet, ...secondaryCabinets];

        // Process cabinets sequentially to handle async operations
        const cabinets: Cabinet[] = [];

        for (const m of metricsForAllCabinets.filter(
            (m) => !scDevices.find((d) => d.satelliteId === m?.satelliteId)?.stale
        )) {
            const cabinetPms =
                mainCabinet?.metrics?.pmcStatus?.filter((p) => Math.floor(p.canAddress / 100) === -m.satelliteId) || [];

            // Pmc Alerts of the this cabinet
            const thisCans = cabinetPms.map((pm) => pm.canAddress);
            const thisPmcs = pmcs.filter((pmc) => thisCans.includes(+(pmc?.details as PowerModuleDetails).can));

            const { alerts: thisPmcAlerts } = mapPmcAlerts<{ all: string[] }>(
                thisPmcs,
                'details.alerts',
                undefined,
                false
            );
            const isSystemAdmin = this.authService.getPermissions().isSystemAdmin;
            const alerts = (thisPmcAlerts.all || []).filter(
                (problem) => isSystemAdmin || !NON_FATAL_PMC_PROBLEMS.includes(problem)
            );

            // Calculate error severity using AlertProblemService
            let errorSeverity: Severity = 'info'; // Default to 'info' (no action required) when no alerts

            // Only check severity if we have PMCs with actual alerts
            const pmcsWithAlerts = thisPmcs.filter(
                (pmc) =>
                    (pmc.details as PowerModuleDetails).alerts && (pmc.details as PowerModuleDetails).alerts.length > 0
            );

            if (pmcsWithAlerts.length > 0) {
                try {
                    const vendorCode = station.station.vendorName?.toLowerCase() || 'kempower';

                    // Use AlertProblemService to get highest severity from devices
                    const severity = await this.alertProblemService.getHighestDeviceSeverityFromDevices(
                        vendorCode,
                        pmcsWithAlerts
                    );
                    if (severity) {
                        errorSeverity = severity;
                    }
                } catch (error) {
                    console.error('Error calculating cabinet error severity:', error);
                    errorSeverity = 'unknown';
                }
            }

            cabinets.push({
                displayId: m === mainCabinet ? 'M' : 'S' + Math.abs(m.satelliteId),
                type: m === mainCabinet ? 'MAIN' : 'SECONDARY',
                id: m?.satelliteId,
                metrics: m?.metrics,
                error: alerts.length > 0 || cabinetPms?.some((p) => p.state === 'ERROR' || p.dcVoltage < 300),
                errorSeverity: errorSeverity,
            } as Cabinet);
        }

        return cabinets.sort((a, b) => b.id - a.id);
    }

    getCabinets$(stationId: string, cabinetId: number) {
        const cabinet: CabinetObservable = {
            id: cabinetId,
            displayId: '',
        };
        return this.station$(stationId).pipe(
            tap((station) => {
                if (cabinetId === 0) {
                    cabinet['displayId'] = 'Main Cabinet';
                } else {
                    cabinet['displayId'] = 'Secondary Cabinet ' + -cabinetId;
                }
                // update all other metrics than power
                const mainMetrics = station.metrics?.find((m) => m.type === 'CABINET' || m.type === 'TROLLEY');
                const secondaryMetrics = station.metrics?.find(
                    (m) => m.type === 'SLAVE_CABINET' && m.satelliteId === cabinetId
                );
                if (mainMetrics?.type === 'TROLLEY') {
                    cabinet['displayId'] = 'Trolley';
                    cabinet['id'] = 0;
                    cabinetId = 0; // change id to 0 if trolley
                }
                const cabId = Math.abs(cabinet['id']);
                if (mainMetrics?.metrics.pmcStatus) {
                    cabinet['pmcStatus'] = mainMetrics.metrics.pmcStatus.filter(
                        (pmc) => Math.floor(pmc.canAddress / 100) === cabId
                    );
                }

                const pmcDevices = station.devices
                    .filter((d) => d.deviceTypeShort === 'pm')
                    .filter((pmc) => Math.floor(+(pmc.details as PowerModuleDetails).can / 100) === cabId);

                cabinet['pmcDevices'] = sortBy(pmcDevices, (device) =>
                    Date.parse((device as any).versionCreatedTimestamp)
                );

                cabinet['cabinetMetrics'] = mainMetrics?.metrics;

                // Only use secondary cabinet metrics if not main cabinet
                // type changed to CABINET because it is used in the UI to determine the type of charger
                if (cabinetId !== 0) {
                    cabinet['cabinetMetrics'] = { ...secondaryMetrics?.metrics, type: 'CABINET' };
                }
            }),
            switchMap(() => {
                return combineLatest([
                    this.connectors$(stationId),
                    this.authService.hasAccessTo$('ev.access').pipe(
                        startWith(false), // do not block
                        distinctUntilChanged(),
                        switchMap((allowed) => {
                            if (allowed) {
                                return this.vehiclesService.vehicles$(); // get latest of vehicles as well
                            } else {
                                return of([] as Vehicle[]);
                            }
                        })
                    ),
                ]).pipe(
                    exhaustMap(([connectors, vehicles]) => {
                        // map pmc usage by tx, optionally with vehicles
                        const activeTxs = connectors
                            .filter((c) => c.status?.status === 'Occupied' || c.tx?.status === 'Charging')
                            .map((evse) => evse.tx);
                        return this.queryPmcPowerUsageByTx(vehicles, activeTxs, cabinetId, connectors);
                    }),
                    tap((dataForPmcs) => {
                        cabinet['dataForPmcs'] = dataForPmcs || {};

                        // Mark cabinet as missing data if null:
                        // This can happen if the data we're looking at is over 3 months old
                        // and has been deleted from the database(timescaledb),
                        // or in some other error conditions.
                        if (dataForPmcs === null) {
                            cabinet.missingData = true;
                        } else {
                            cabinet.missingData = false;
                        }
                    }),
                    map(() => {
                        return cabinet;
                    })
                );
            })
        );
    }

    private async queryPmcPowerUsageByTx(
        vehicles: Vehicle[],
        txs: LatestTransactionRow[],
        cabinetId: number,
        connectors: GetEvsesForChargingStationResponse
    ): Promise<DataForPmcs> {
        const txIds = txs.map((tx) => tx.txId);
        if (!txIds.length) {
            return [];
        }

        try {
            const res = await this.transactionApi.getTransactionLatestSample({ txIds }, { at: this.seekTime });

            // here we rely on the internal charger CombinedState..
            const txSessions = sortBy(
                Object.keys(res.samples)
                    .map((txId) => {
                        const session = res.samples[txId].rawSession;
                        return session;
                    })
                    .filter(Boolean),
                'timestamp'
            );
            if (!txSessions.length) {
                // No raw session data found - return null:
                // This can happen if the data we're looking at is over 3 months old
                // and has been deleted from the database (timescaledb),
                // or in some other error conditions.
                return null;
            }

            // calculate power for each pmc
            const dataForPmcs: DataForPmcs = {};
            for (const session of txSessions) {
                if (session.session?.isCharging) {
                    const pmcs = session.session.pmc || [];
                    for (const pmc of pmcs) {
                        const { cabId, canRelative } = extractPmcCanId(pmc.can);
                        // consider only the power channels for this cabinet
                        if (cabId === Math.abs(cabinetId)) {
                            if (!dataForPmcs[canRelative]) {
                                dataForPmcs[canRelative] = { power: 0, current: 0, vehicle: null, connector: null };
                            }
                            dataForPmcs[canRelative].power = Math.ceil(pmc.powerW / 1000) || undefined;
                            dataForPmcs[canRelative].current = pmc.current || undefined;
                            dataForPmcs[canRelative].vehicle = vehicles.find(
                                (v) => v.evMacAddress === session.session.vehicleMacAddress
                            );
                            dataForPmcs[canRelative].connector = connectors.find(
                                (c) => String(c.evseId) === String(session.satelliteId + '' + session.connectorId)
                            );
                        }
                    }
                }
            }
            return dataForPmcs;
        } catch (e) {
            console.error(e);
            return [];
        }
    }

    getCurrentlyUsedPower(station: GetChargingStationResponse, evses: GetEvsesForChargingStationResponse): number {
        return getStationCurrentPower(evses, station);
    }

    /**
     *
     * @param station
     * @returns an array power modules which are not marked as stale
     */
    getActivePowerModules(station: GetChargingStationResponse) {
        const activePowerModules = station.devices
            .filter((device) => device.deviceTypeShort === 'pm' && !device.stale)
            .map((device) => {
                // Map to get correct typing
                const details = device.details as PowerModuleDetails;
                return {
                    ...device,
                    details,
                };
            });

        return activePowerModules || [];
    }

    getStationPower(station: GetChargingStationResponse) {
        let powerKw = 0;
        this.getActivePowerModules(station).forEach((e) => (powerKw += e.details.powerKw || 0));

        // Input current limitation
        const inputCurrent = station.settings?.configuration?.PmcCtrlr?.MaximumInputCurrent;
        let limitedMaxPowerKw: number;
        if (inputCurrent > 0) {
            const inputVoltage = station.settings?.configuration?.PmcCtrlr?.InputVoltage;

            const limitedMaxPowerKw_ = acAtoDcKW(inputCurrent, inputVoltage);
            // show the limited input current only when
            // 1) Not trolley, or Trolley and inputCurrent <= 63,
            // and
            // 2) Limit is 5 kW less than powerkW
            const type = this.getStationType(station);
            if (type !== 'TROLLEY' || inputCurrent <= 63) {
                const delta = powerKw - limitedMaxPowerKw_;
                if (delta >= 5) {
                    limitedMaxPowerKw = limitedMaxPowerKw_;
                }
            }
        }

        return {
            powerKw,
            limitedMaxPowerKw,
            effectiveMaxPowerKw: limitedMaxPowerKw || powerKw,
        };
    }

    getMainMetrics(station: GetChargingStationResponse) {
        return getMainMetrics(station);
    }

    /**
     * Returns whether C-series Dynamic Module is in use and the type/number of outputs (R4 or R8).
     */
    getDynamicsType(station: GetChargingStationResponse) {
        const dynamics: string = station?.structure?.structure?.dynamic_routing?.type;
        if (dynamics?.startsWith?.('R')) {
            return dynamics.replace('R', 'D');
        }

        return undefined;
    }

    getSatellites(station: GetChargingStationResponse, connectors: GetEvsesForChargingStationResponse) {
        const satellites: Satellite[] = [];
        const devicesSatellites = keyBy(
            station?.devices?.filter(
                (d) => (d?.deviceTypeShort === 'sc' || d?.deviceTypeShort === 'cs') && d?.satelliteId > 0
            ),
            'satelliteId'
        );

        // all satellites reported in metrics
        const satelliteMetrics =
            station?.metrics?.filter(
                (m) => m.type === 'SATELLITE' || m.type === 'TROLLEY' || m.type === 'STANDALONE'
            ) || [];
        for (const m of satelliteMetrics) {
            const satelliteConnectors = connectors?.filter(
                (c) => evseIdParser(+c.evseId).satelliteId === m.satelliteId
            );
            const deviceSerial = devicesSatellites[m.satelliteId]?.serialNumber;
            const metricsSerial = m.metrics?.serialNumber;
            satellites.push({
                satelliteId: m.satelliteId,
                actualType: this.getActualStationType(station),
                type: m.type,
                connectors: satelliteConnectors,
                metrics: m.metrics,
                station,
                serialNumber:
                    deviceSerial && !deviceSerial?.toLowerCase()?.includes('error!')
                        ? deviceSerial
                        : metricsSerial && !metricsSerial?.toLowerCase()?.includes('error!')
                          ? metricsSerial
                          : null,
            });
        }

        // all satellites reported in connectors but not in metrics (missing data?)
        const groupedConnectors = Object.entries(groupBy(connectors, (c) => evseIdParser(+c.evseId).satelliteId));
        for (const [satelliteId, satelliteConnectors] of groupedConnectors) {
            if (!satellites.find((s) => s.satelliteId === +satelliteId)) {
                const type = this.getStationType(station);
                const deviceSerial = devicesSatellites[+satelliteId]?.serialNumber;
                satellites.push({
                    satelliteId: +satelliteId,
                    type: type === 'TROLLEY' || type === 'STANDALONE' ? type : 'SATELLITE',
                    actualType: this.getActualStationType(station),
                    connectors: satelliteConnectors,
                    metrics: undefined,
                    station,
                    serialNumber:
                        deviceSerial && !deviceSerial?.toLowerCase()?.includes('error!') ? deviceSerial : null,
                });
            }
        }

        // filter duplicating satellites
        const duplicateFiltered: Satellite[] = [];
        satellites.forEach((sat) => {
            const satId = sat.satelliteId;
            const currentIndex = duplicateFiltered.findIndex((sat) => sat.satelliteId === satId);

            if (currentIndex >= 0) {
                const currentTs = moment(duplicateFiltered[currentIndex]?.metrics?.ts);
                // Overwrite with more recent satellite
                if (currentTs.isBefore(sat?.metrics?.ts)) {
                    duplicateFiltered.splice(currentIndex, 1, sat);
                }
            } else {
                duplicateFiltered.push(sat);
            }
        });

        const satsWithTooltip = duplicateFiltered.map((s) => {
            return { ...s, tooltipText: this.getSatelliteTooltipText(s) };
        });

        return sortBy(satsWithTooltip, (s) => s.satelliteId);
    }
    getSatelliteTooltipText(satellite: Satellite): string {
        let tooltip = '';
        switch (satellite.actualType) {
            case 'UNKNOWN':
                tooltip =
                    $localize`:Charger map|Satellite type:3rd Party Charger` +
                    ' ' +
                    (satellite?.station?.station?.stationId || '');
                break;
            case 'VIRTUAL':
                tooltip = $localize`:Charger map|Satellite type:Virtual Charger`;
                break;
            case 'TROLLEY':
                tooltip = $localize`:Charger map|Satellite type:Movable Charger`;
                break;
            default:
                tooltip =
                    $localize`:Charger map|Satellite type:Satellite` +
                    (satellite.satelliteId ? ' ' + satellite.satelliteId : '');
                break;
        }
        return tooltip;
    }

    transactionUpdated$(txId: string, startImmediately?: boolean): Observable<any> & Observable<number> {
        const txUpdated$ = merge(
            this.notifications.notification$.pipe(
                filter(
                    (n) =>
                        n.type === 'ChargingStationUpdated' &&
                        n.changedSchemas?.includes('tx') &&
                        n.events?.some((e) => (e?.data as TxCsmsEventBody)?.txId === txId)
                ),
                mapTo(void 0)
            ),
            FORCE_REFRESH_INTERVAL ? interval(FORCE_REFRESH_INTERVAL_MS) : EMPTY
        );

        // return startImmediately ? txUpdated$.pipe(startWith(void 0)) : txUpdated$; //startWith(void 0) depreciated
        return startImmediately ? txUpdated$.pipe(startWith(0)) : txUpdated$;
    }

    transaction$(txId: string): Observable<Transaction> {
        return this.transactionUpdated$(txId).pipe(
            startWith([]),
            switchMap(() => this.transactionApi.getTransaction(txId)),
            map((res) => res.transaction),
            retryWithDelay()
        );
    }

    connectorStats$(stationId: string, evseId: number | string): Observable<ConnectorStats> {
        return this.stationRefreshRequest$(stationId).pipe(
            startWith([]),
            exhaustMap(() => this.evseApi.getEvseStats(stationId, `${evseId}`)),
            map((statsRes) => {
                const stats: ConnectorStats = {
                    insertionCycles: statsRes.insertionsCount,
                    unstartedRate: statsRes.unstartedRate,
                    energyExpendedKwh: statsRes.energyExpendedKwh,
                    operatingHours: statsRes.operatingHours,
                    daysSinceFirstUse: statsRes.daysSinceFirstUse,
                };

                return stats;
            }),
            retryWithDelay()
        );
    }

    connectorInfo$(stationId: string, evseId: number | string) {
        return combineLatest([this.station$(stationId), this.connectors$(stationId)]).pipe(
            map(([station, connectors]) => {
                const { satelliteId, connectorId } = evseIdParser(+evseId);
                const info: ConnectorInfo = {
                    satelliteId,
                    connectorId,
                    connectorSequenceNo: (() => {
                        const index = connectors.findIndex(
                            (c) => c.evseId === (isNaN(evseId as number) ? evseId : String(evseId))
                        );
                        return index === -1 ? undefined : 1 + index;
                    })(),
                    cableLength: undefined,
                    temp: undefined,
                };

                // cable length
                const satelliteMetrics = station.metrics.find((m) => m.satelliteId === satelliteId)?.metrics;
                if (satelliteMetrics?.productCode) {
                    const productInfo = parseCode(satelliteMetrics.productCode);
                    info.cableLength = productInfo?.connectors?.[0]?.cableLength;
                }

                // temperature
                if (satelliteMetrics?.pinTemps) {
                    if (connectorId === 1) {
                        info.temp = satelliteMetrics.pinTemps.ccs1;
                    } else if (connectorId === 2) {
                        info.temp = satelliteMetrics.pinTemps.ccs2;
                    }
                }

                // connector sequence number (also used in ocpp1.6j)
                return info;
            })
        );
    }

    _commissioning$: { [stationId: string]: Observable<GetStationCommissioningResponse> } = {};
    commissioning$(stationId: string): Observable<GetStationCommissioningResponse> {
        if (!this._commissioning$[stationId]) {
            const refresh$ = merge(
                this.refreshRequested$,
                this.notifications.notification$.pipe(
                    filter(
                        (n) =>
                            n['stationId'] === stationId &&
                            n.type === 'ChargingStationUpdated' &&
                            n.changedSchemas?.includes('commissioning')
                    )
                )
            );
            this._commissioning$[stationId] = refresh$.pipe(
                startWith(0),
                switchMap(() => from(this.stationsApi.getStationCommissioning(stationId)))
            );
        }
        return this._commissioning$[stationId];
    }

    seek(ts: string) {
        this._seekTime$.next(ts);
    }

    resetSeek() {
        this._seekTime$.next(null);
    }

    /**
     * Returns true if user has seeked more than 3 months in the past.
     * This is used to determine if we should show a warning about some data being unavailable.
     * Some data, like raw sessions for transaction samples, older than 3 months is not available in the databases.
     */
    isDataTooOldForDetailedMetrics(time = this.seekTime) {
        const MISSING_DATA_DUE_TO_OLD_DATA_CUTOFF_DAYS = 3 * 30; // 3 months
        const MISSING_DATA_DUE_TO_OLD_DATA_CUTOFF_MS = MISSING_DATA_DUE_TO_OLD_DATA_CUTOFF_DAYS * 24 * 60 * 60 * 1000;
        return Date.now() - Date.parse(time) > MISSING_DATA_DUE_TO_OLD_DATA_CUTOFF_MS;
    }

    async fetchHistoricStation(
        stationId: string,
        seekTime: string,
        station: GetChargingStationResponse
    ): Promise<GetChargingStationResponse> {
        const res = await this.reportingApi.query(
            {
                query: {
                    indexName: 'schema-systemmetricshistory,schema-connectionhistory',
                    parameters: [
                        { term: { stationId: stationId } },
                        { terms: { schema: ['systemMetricsHistory', 'connectionHistory'] } },
                        { range: { timestamp: { lte: seekTime } } },
                    ],
                    size: 0,
                    disableEsParser: true,
                    aggregations: {
                        bySatelliteId: {
                            terms: { field: 'satelliteId', size: 30, missing: -10 },
                            aggs: {
                                bySchema: {
                                    terms: { field: 'schema' },
                                    aggs: {
                                        doc: {
                                            top_hits: {
                                                size: 1,
                                                sort: { timestamp: { order: 'desc' } },
                                            },
                                        },
                                    },
                                },
                            },
                        },
                    },
                },
            },
            { src: 'station-historic' }
        );
        const historicStation: GetChargingStationResponse = { ...station, metrics: [], connection: null };
        for (const b of res.response.bySatelliteId.buckets) {
            if (b.key === -10) {
                // not system metrics
                const connection = b.bySchema.buckets.find((s) => s.key === 'connectionHistory')?.doc.hits.hits[0]
                    ._source;
                if (connection) {
                    historicStation.connection = connection;
                    if (historicStation.connection.connectionEndTime >= seekTime) {
                        historicStation.connection.connectionEndTime = null;
                    }
                }

                // fetch historic devices also..
                const historicDevices = await this.fetchHistoricDevices(stationId, seekTime);
                if (historicDevices) {
                    historicStation.devices = historicDevices;
                }
            } else {
                // system metrics
                const systemMetrics = b.bySchema.buckets.find((s) => s.key === 'systemMetricsHistory')?.doc.hits.hits[0]
                    ._source as GetChargingStationResponse['metrics'][0];
                if (systemMetrics) {
                    historicStation.metrics.push(systemMetrics);
                }
            }
        }

        return historicStation;
    }

    async fetchHistoricDevices(stationId: string, seekTime: string): Promise<GetChargingStationResponse['devices']> {
        const maxDeviceConfigurationIdRes = await this.reportingApi.query(
            {
                query: {
                    indexName: 'schema-devicehistory',
                    size: 0,
                    parameters: [
                        { term: { schema: 'deviceHistory' } },
                        { term: { stationId } },
                        { range: { versionCreatedTimestamp: { lte: seekTime } } },
                    ],
                    aggregations: {
                        maxConfigId: {
                            max: { field: 'configurationId.long' },
                        },
                    },
                    disableEsParser: true,
                },
            },
            { src: 'station-historic-devices-maxid' }
        );
        const maxDeviceConfigurationId = maxDeviceConfigurationIdRes?.response?.maxConfigId?.value;
        if (maxDeviceConfigurationId) {
            const devices = await this.reportingApi.query(
                {
                    query: {
                        indexName: 'schema-devicehistory',
                        size: 100,
                        parameters: [
                            { term: { schema: 'deviceHistory' } },
                            { term: { stationId } },
                            { term: { 'configurationId.long': maxDeviceConfigurationId } },
                            { range: { versionCreatedTimestamp: { lte: seekTime } } },
                        ],
                    },
                },
                { src: 'station-historic-devices' }
            );
            if (devices.response.length) {
                // if all OK, set historic devices (e.g. cabinets status) back to as it was back then
                return this.filterByLatestTimestamp(devices.response).map((d) => ({ ...d._source, stale: false }));
            }
        }

        return undefined;
    }
    filterByLatestTimestamp(response: any) {
        const result = [];
        const seenSerialNumbers = new Set<string>();

        // Sort by `timestamp` with the latest first
        const sortedResults = sortBy(response, (hit) => {
            return new Date(hit._source.timestamp).getTime();
        }).reverse(); // reverse to get descending order

        // Use a Set to track serial numbers and filter duplicates
        sortedResults.forEach((s) => {
            const serialNumber = s._source.serialNumber;
            if (!seenSerialNumbers.has(serialNumber)) {
                result.push(s);
                seenSerialNumbers.add(serialNumber); // Mark the serial number as seen
            }
        });

        return result;
    }

    async fetchHistoricConnectors(
        stationId: string,
        seekTime: string,
        connectors: GetEvsesForChargingStationResponse
    ): Promise<GetEvsesForChargingStationResponse> {
        const queryFn: (schema: string[], indexName?: RawQuery['indexName'], indexPartition?: string[]) => RawQuery = (
            schema,
            indexName,
            indexPartition
        ) => ({
            indexName,
            indexPartition,
            parameters: [
                { term: { stationId: stationId } },
                { terms: { schema } },
                {
                    bool: {
                        should: [
                            { range: { updateTime: { lte: seekTime } } },
                            { range: { sampleTime: { lte: seekTime } } },
                            { range: { timestamp: { lte: seekTime } } },
                        ],
                    },
                }, // TODO
            ],
            size: 0,
            disableEsParser: true,
            aggregations: {
                byEvseId: {
                    terms: { field: 'evseId', size: 20 },
                    aggs: {
                        bySchema: {
                            terms: { field: 'schema' },
                            aggs: {
                                doc: {
                                    top_hits: {
                                        size: 1,
                                        sort: {
                                            timestamp: { order: 'desc' },
                                        },
                                    },
                                },
                            },
                        },
                    },
                },
            },
        });
        const res = await this.reportingApi.query(
            {
                query: queryFn(['statusHistory', 'tx']),
            },
            { src: 'station-historic-tx' }
        );
        const allTransactions: Transaction[] = [];
        for (const b of res.response.byEvseId.buckets) {
            const tx = b.bySchema.buckets.find((s) => s.key === 'tx')?.doc.hits.hits[0]._source as Transaction;
            if (tx != null) {
                allTransactions.push(tx);
            }
        }
        const txIds = allTransactions.map((tx) => tx.txId);
        const txSamples = await this.transactionApi.getTransactionLatestSample({ txIds }, { at: seekTime });
        let historicConnectors: GetEvsesForChargingStationResponse = [];
        for (const b of res.response.byEvseId.buckets) {
            const evseId = b.key;
            const status = b.bySchema.buckets.find((s) => s.key === 'statusHistory')?.doc.hits.hits[0]
                ._source as StatusRow;
            const tx = b.bySchema.buckets.find((s) => s.key === 'tx')?.doc.hits.hits[0]._source as Transaction;
            const txSample = tx?.txId && txSamples?.samples ? txSamples.samples[tx.txId] : null;

            // tslint:disable-next-line: triple-equals
            const connector = connectors.find((c) => c.evseId == evseId); // can be either string or number
            if (connector && status && txSample && tx) {
                if (tx.status === 'Ended' && seekTime > tx.endTime) {
                    historicConnectors.push({
                        ...connector,
                        status: status as any,
                        tx: undefined,
                    });
                } else {
                    const diffToSeekTime = Date.parse(seekTime) - Date.parse(txSample.sampleTime);
                    historicConnectors.push({
                        ...connector,
                        status: status as any,
                        tx: {
                            ...tx,
                            updateTime: new Date(Date.now() - diffToSeekTime).toISOString(),
                            soc: txSample.stateOfCharge,
                            power: txSample.power,
                            powerKw: txSample.power / 1000,
                            chargedEnergyKwh: txSample.cumulativeEnergyKwh,
                            status: txSample.status || txSampleStatusHeuristic(tx, txSample),
                            current: txSample.current,
                            voltage: txSample.voltage,
                            chargingForecastTimes: txSample.chargingForecastTimes,
                            powerLimitation: txSample.powerLimitation,
                            evIdentify: tx.evIdentify,
                        } as any,
                    });
                }
            } else if (connector) {
                historicConnectors.push({
                    ...connector,
                    status: status || (connector.status as any),
                    tx: undefined,
                });
            }
        }
        historicConnectors = sortBy(historicConnectors, (h) => h.evseId);

        return historicConnectors;
    }

    async listStations(options?: { stationIds?: string[]; specificFieldsOnly?: string[]; size?: number }) {
        const query: QueryRequest = {
            query: {
                indexName: 'schema-station',
                size: options?.size || 9999,
                parameters: [{ term: { schema: 'station' } }],
                source: options?.specificFieldsOnly,
            },
        };

        if (options?.stationIds) {
            query.query.parameters.push({ term: { stationId: options?.stationIds } });
        }
        const res = await this.reportingApi.query(query, { src: 'list-stations' });
        const stations = (res.response || []).map((r) => r._source);
        return stations;
    }

    getActualStationType(station: GetChargingStationResponse): StationType {
        const vcip = _get(station, 'metrics[0].metrics.vcip');
        const connectionType = station?.connection?.type;
        if (vcip) return 'VIRTUAL';
        if (connectionType === 'ocpi') return 'UNKNOWN';
        return this.getStationType(station);
    }
}

/**
 * Given an OCPP EVSE ID like 21, split it into parts => satellite id 2, connector id 1.
 */
export function evseIdParser(evseId: number) {
    const connectorId = evseId % 10;
    const satelliteId = (evseId - connectorId) / 10;
    return { satelliteId, connectorId };
}

export function evseIdGenerator(satelliteId: number, connectorId: number) {
    return satelliteId * 10 + connectorId;
}

function acAtoDcKW(acA: number, inputVoltage: number) {
    const INPUT_VOLTAGE_COEFFICIENT = (inputVoltage ?? 400) / 400;
    const KEMPOWER_EFFICIENCY_COEFFICIENT = 0.94 * 0.93;

    const AC_CURRENT_TO_DC_POWER_CONVERSION_RATIO =
        INPUT_VOLTAGE_COEFFICIENT * (3 * 230 * KEMPOWER_EFFICIENCY_COEFFICIENT);

    return Math.round((acA * AC_CURRENT_TO_DC_POWER_CONVERSION_RATIO) / 1000);
}

function getStationCurrentPower(
    connectors: GetEvsesForChargingStationResponse,
    stationBasics: GetChargingStationResponse
) {
    const powerByTallyingActiveTx = getStationCurrentPowerByTx(connectors);
    if (powerByTallyingActiveTx === undefined) {
        return getStationCurrentPowerByMetrics(stationBasics);
    } else {
        return powerByTallyingActiveTx;
    }
}

function getStationCurrentPowerByTx(connectors: GetEvsesForChargingStationResponse) {
    const UNRELIABLE_ACTIVE_TX_DURATION_MS = 120 * 1000;
    let latestTxUnreliable = false;
    const currentPowerkW = connectors.reduce((acc, c) => {
        const tx = c.tx;
        let activePowerKw = 0;
        if (tx?.powerKw >= 0 && tx?.status === 'Charging' && !tx.endTime && !tx.chargingEndTime) {
            const durationSinceUpdateMs = Date.now() - Date.parse(tx.updateTime);
            if (durationSinceUpdateMs < UNRELIABLE_ACTIVE_TX_DURATION_MS) {
                activePowerKw = tx.powerKw;
            } else {
                latestTxUnreliable = true;
            }
        }
        return acc + activePowerKw;
    }, 0);
    if (!latestTxUnreliable && currentPowerkW >= 0) {
        return currentPowerkW;
    } else {
        return undefined;
    }
}

function getStationCurrentPowerByMetrics(station: GetChargingStationResponse) {
    return getMainMetrics(station)?.metrics?.powerStats?.max / 1000 || 0;
}

function getMainMetrics(station: GetChargingStationResponse) {
    return station.metrics?.find((m) => m.type === 'CABINET' || m.type === 'TROLLEY' || m.type === 'STANDALONE');
}

/**
 * Some older samples might be missing the 'status' field.
 * Default to Charging to be able to display them, if between chargingStartTime and chargingEndTime.
 */
function txSampleStatusHeuristic(tx: Transaction, txSample: TransactionSample): LatestTransactionRow['status'] {
    if (
        txSample.sampleTime >= tx.chargingStartTime &&
        (!tx.chargingEndTime || txSample.sampleTime <= tx.chargingEndTime)
    ) {
        return 'Charging';
    } else if (txSample.sampleTime >= tx.endTime) {
        return 'Ended';
    }

    return 'PluggedInIdle';
}
